import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from '../../services/clinical-onboarding.service';
import { ToastService } from '../../services/general/toast.service';

/**
 * DBEK-395 (ridisegno "candidato"): registrazione rapida di uno o piu' futuri
 * clinici, disponibile GIA' a survey ancora in bozza (piano
 * §"registrare i futuri medici prima che la survey sia effettivamente partita") -
 * mirror del pattern "Aggiungi membro" gia' esistente (form semplice, invio diretto,
 * niente wizard multi-step), ma per piu' righe alla volta dato che il backend accetta
 * gia' un array in una sola chiamata (ClinicalOnboardingController::registerCandidates).
 * La versione bulk-CSV via foglio Excel (come ModalImportSurveyTeamComponent) resta
 * fuori da questo ticket: quel componente e' costruito attorno alla preview/mapping
 * lato server per i team member reali, non ancora disponibile per i candidati - un
 * eventuale riuso richiederebbe un endpoint di preview dedicato non ancora costruito.
 */
@Component({
  selector: 'app-modal-register-clinical-onboarding-candidates',
  templateUrl: './modal-register-clinical-onboarding-candidates.component.html',
  styleUrls: ['./modal-register-clinical-onboarding-candidates.component.scss'],
  standalone: false,
})
export class ModalRegisterClinicalOnboardingCandidatesComponent {
  loading = false;
  form: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<ModalRegisterClinicalOnboardingCandidatesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { surveyId: string | number },
    private fb: FormBuilder,
    private clinicalOnboardingService: ClinicalOnboardingService,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {
    this.form = this.fb.group({
      candidates: this.fb.array([this.newRow()]),
    });
  }

  get rows(): FormArray {
    return this.form.get('candidates') as FormArray;
  }

  private newRow(): FormGroup {
    return this.fb.group({
      name: ['', Validators.required],
      last_name: [''],
      email: ['', [Validators.required, Validators.email]],
    });
  }

  addRow(): void {
    this.rows.push(this.newRow());
  }

  removeRow(index: number): void {
    if (this.rows.length > 1) {
      this.rows.removeAt(index);
    }
  }

  /**
   * Il backend fa upsert per (survey_history_id, email): righe con la stessa email
   * si sovrascriverebbero silenziosamente a vicenda, quindi blocchiamo il submit
   * finche' non vengono corrette (confronto case-insensitive/trim, coerente con
   * come le email vengono normalmente confrontate).
   */
  private markDuplicateEmails(): boolean {
    const emailControls = this.rows.controls.map((row) => (row as FormGroup).get('email'));
    const normalizedEmails = emailControls.map((control) => (control.value || '').trim().toLowerCase());

    let hasDuplicates = false;
    emailControls.forEach((control, index) => {
      const email = normalizedEmails[index];
      const isDuplicate = !!email && normalizedEmails.some((other, otherIndex) => otherIndex !== index && other === email);

      const errors = { ...(control.errors || {}) };
      if (isDuplicate) {
        hasDuplicates = true;
        errors.duplicateEmail = true;
      } else {
        delete errors.duplicateEmail;
      }
      control.setErrors(Object.keys(errors).length ? errors : null);
    });

    return hasDuplicates;
  }

  onSubmit(): void {
    const hasDuplicateEmails = this.markDuplicateEmails();

    if (this.form.invalid || hasDuplicateEmails) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.clinicalOnboardingService.registerCandidates(
      this.data.surveyId,
      this.rows.value,
      (result) => {
        this.loading = false;
        this.toastService.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('modals.register-clinical-onboarding-candidates.success', {
            added: result.added,
            updated: result.updated,
          }),
        );
        this.dialogRef.close(true);
      },
      () => {
        this.loading = false;
      },
    );
  }

  closeDialogue(): void {
    this.dialogRef.close();
  }
}
